import * as _ from "lodash";
import { Button } from "@mui/joy";
import { useEffect, useRef, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import { isDev } from "config";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { useRecoilState } from "recoil";
import { permanentDataAtom } from "recoil/permanentDataAtom";
import useGetEventFromApp from "hooks/useGetEventFromApp";

export default function AudioButton({
  isCurrentTask,
  url,
  initialTime,
  setData,
  index,
  lineIndex,
  cellIndex,
  isCoach,
  disabled,
}: {
  isCurrentTask: boolean;
  url: string;
  initialTime: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  index: number;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  disabled: boolean;
}) {
  const { t: t_ui } = useTranslation("translation", {
    keyPrefix: "ui",
  });
  const audioRef = useRef<HTMLAudioElement>(null);
  const [play, setPlay] = useState(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(initialTime);

  const updateCurrentTimeData = () => {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);
        const currentContent =
          data_temp[index].lines[lineIndex][cellIndex].content || {};

        data_temp[index].lines[lineIndex][cellIndex].content = {
          ...currentContent,
          currentTime: audioRef.current?.currentTime,
        };
        if (!_.isEqual(data, data_temp)) {
          return data_temp;
        }
        return data;
      }
      return data;
    });
  };
  const debouncedUpdateTimeHandler = debounce(updateCurrentTimeData, 1000);

  useEffect(() => {
    if (play) {
      debouncedUpdateTimeHandler();
    }
  }, [currentTime, play]);

  // 재생
  const start = () => {
    if (audioRef.current) {
      audioRef.current.play();
    }
    setPlay(true);
  };
  // 일시 정지
  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlay(false);
  };

  function handleEnded() {
    setPlay(false);
    if (!isCoach) {
      setData((data) => {
        if (data) {
          const data_temp = _.cloneDeep(data);
          const currentContent =
            data_temp[index].lines[lineIndex][cellIndex].content || {};

          data_temp[index].lines[lineIndex][cellIndex].content = {
            ...currentContent,
            isEnded: true,
          };
          data_temp[index].autoOpenNext = true;
          if (!_.isEqual(data, data_temp)) {
            return data_temp;
          }
          return data;
        }
        return data;
      });
    }
  }

  function handleLoadedData() {
    if (audioRef.current) {
      audioRef.current.currentTime = initialTime;
    }
  }
  function handleDurationChange() {
    setDuration(parseInt(`${audioRef.current?.duration}`));
  }

  function handleTimeUpdate() {
    setCurrentTime(parseInt(`${audioRef.current?.currentTime}`));
  }
  function handlePause() {
    setPlay(false);
  }

  // ended 에 안걸리는 경우 대비
  // useEffect(() => {
  //   if (currentTime > 0 && currentTime === duration) {
  //     handleEnded();
  //   }
  // }, [currentTime, duration]);

  const [permanantDataState, setPermanantDataState] =
    useRecoilState(permanentDataAtom);

  const parsedMessageFromApp = useGetEventFromApp();

  useEffect(() => {
    if (
      isCurrentTask &&
      parsedMessageFromApp?.event === "timeSpentInBackground"
    ) {
      const seconds = parsedMessageFromApp.params.seconds;

      if (!play) {
        setCurrentTime(
          Math.min(
            permanantDataState?.audioCurrentTime || 0 + seconds,
            duration
          )
        );
        // start();
      }
    }
  }, [parsedMessageFromApp]);

  const handleVisibilityChange = () => {
    if (document.hidden) {
      setPermanantDataState((state) => ({
        ...state,
        audioCurrentTime: currentTime,
      }));
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      audioRef.current.addEventListener("loadeddata", handleLoadedData);
      audioRef.current.addEventListener("durationchange", handleDurationChange);
      audioRef.current.addEventListener("timeupdate", handleTimeUpdate);
      audioRef.current.addEventListener("ended", handleEnded);
      audioRef.current.addEventListener("pause", handlePause);
    }

    return () => {
      if (audioRef.current) {
        setPermanantDataState((state) => ({
          ...state,
          audioCurrentTime: undefined,
        }));
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        audioRef.current.removeEventListener("loadeddata", handleLoadedData);
        audioRef.current?.removeEventListener(
          "durationchange",
          handleDurationChange
        );
        audioRef.current?.removeEventListener("timeupdate", handleTimeUpdate);
        audioRef.current?.removeEventListener("ended", handleEnded);
        audioRef.current?.removeEventListener("pause", handlePause);
      }
    };
  }, []);

  function parseSecondsToFormat(seconds: number) {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(
      2,
      "0"
    )}`;
  }

  return (
    <div>
      <div className={`${!isDev && "hidden"}`}>
        <audio ref={audioRef} src={url} controls></audio>
      </div>
      <Button
        disabled={disabled}
        fullWidth
        onClick={(event) => {
          if (play) {
            stop();
          } else {
            start();
          }
        }}
        variant={play ? "soft" : "solid"}
        size={"md"}
      >
        {play
          ? `${parseSecondsToFormat(currentTime)} / ${parseSecondsToFormat(
              duration
            )}`
          : t_ui(currentTime === 0 ? "음성 안내 재생하기" : "이어서 재생하기")}
      </Button>
    </div>
  );
}
